// src/services/seasonStatsServices.js

import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/season-stats`;

/* -------------------------------------------------------------------------- */
/*                              Fonctions utiles                              */
/* -------------------------------------------------------------------------- */

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");

  return {
    Authorization: `Bearer ${token}`,
  };
};

/* -------------------------------------------------------------------------- */
/*                                    GET                                     */
/* -------------------------------------------------------------------------- */

/**
 * Récupérer toutes les saisons statistiques.
 */
export const getSeasonStats = async () => {
  try {
    const response = await axios.get(
      `${apiUrl}/getSeasonStats`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la récupération des statistiques par saison :",
      error?.response?.data || error,
    );

    throw error;
  }
};

/**
 * Récupérer une saison statistique par son ID.
 */
export const getSeasonStatById = async (id) => {
  if (!id) {
    throw new Error(
      "L'identifiant de la saison statistique est obligatoire.",
    );
  }

  try {
    const response = await axios.get(
      `${apiUrl}/getSeasonStatById/${id}`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      `Erreur lors de la récupération de la saison statistique ${id} :`,
      error?.response?.data || error,
    );

    throw error;
  }
};

/**
 * Récupérer les statistiques à partir du libellé de saison.
 *
 * Exemple :
 * getSeasonStatBySeason("2025-2026")
 */
export const getSeasonStatBySeason = async (
  season,
) => {
  if (!season) {
    throw new Error(
      "La saison est obligatoire.",
    );
  }

  try {
    const response = await axios.get(
      `${apiUrl}/getSeasonStatBySeason/${encodeURIComponent(
        season,
      )}`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      `Erreur lors de la récupération des statistiques de la saison ${season} :`,
      error?.response?.data || error,
    );

    throw error;
  }
};

/**
 * Récupérer tous les breakdowns d'une saison statistique.
 */
export const getBreakdownsBySeasonStat = async (
  seasonStatId,
) => {
  if (!seasonStatId) {
    throw new Error(
      "L'identifiant de la saison statistique est obligatoire.",
    );
  }

  try {
    const response = await axios.get(
      `${apiUrl}/getBreakdownsBySeasonStat/${seasonStatId}`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la récupération des détails statistiques :",
      error?.response?.data || error,
    );

    throw error;
  }
};

/**
 * Récupérer un type précis de breakdown.
 *
 * Exemples :
 *
 * getBreakdownsByType(3, "GENDER")
 * getBreakdownsByType(3, "AGE_RANGE")
 * getBreakdownsByType(3, "GROUP")
 */
export const getBreakdownsByType = async (
  seasonStatId,
  breakdownType,
) => {
  if (!seasonStatId) {
    throw new Error(
      "L'identifiant de la saison statistique est obligatoire.",
    );
  }

  if (!breakdownType) {
    throw new Error(
      "Le type de statistique est obligatoire.",
    );
  }

  try {
    const response = await axios.get(
      `${apiUrl}/getBreakdownsByType/${seasonStatId}/${encodeURIComponent(
        breakdownType,
      )}`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      `Erreur lors de la récupération des statistiques ${breakdownType} :`,
      error?.response?.data || error,
    );

    throw error;
  }
};

/**
 * Récupérer une ligne statistique par son ID.
 */
export const getBreakdownById = async (id) => {
  if (!id) {
    throw new Error(
      "L'identifiant de la statistique est obligatoire.",
    );
  }

  try {
    const response = await axios.get(
      `${apiUrl}/getBreakdownById/${id}`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      `Erreur lors de la récupération de la statistique ${id} :`,
      error?.response?.data || error,
    );

    throw error;
  }
};

/**
 * Récupérer l'évolution d'une statistique
 * sur plusieurs saisons.
 *
 * Exemples :
 *
 * getStatEvolution("TOTAL", "ADHERENTS")
 * getStatEvolution("GENDER", "MALE")
 * getStatEvolution("GENDER", "FEMALE")
 * getStatEvolution("DISCIPLINE", "JUDO")
 * getStatEvolution("GROUP", "GROUP_5")
 */
export const getStatEvolution = async (
  breakdownType,
  code,
) => {
  if (!breakdownType) {
    throw new Error(
      "Le type de statistique est obligatoire.",
    );
  }

  if (!code) {
    throw new Error(
      "Le code de la statistique est obligatoire.",
    );
  }

  try {
    const response = await axios.get(
      `${apiUrl}/getStatEvolution/${encodeURIComponent(
        breakdownType,
      )}/${encodeURIComponent(code)}`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      `Erreur lors de la récupération de l'évolution ${breakdownType}/${code} :`,
      error?.response?.data || error,
    );

    throw error;
  }
};

/**
 * Récupérer les statistiques permettant
 * de comparer toutes les saisons.
 */
export const getSeasonStatsComparison = async () => {
  try {
    const response = await axios.get(
      `${apiUrl}/getSeasonStatsComparison`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la récupération de la comparaison des saisons :",
      error?.response?.data || error,
    );

    throw error;
  }
};

/* -------------------------------------------------------------------------- */
/*                                   POST                                     */
/* -------------------------------------------------------------------------- */

/**
 * Créer une nouvelle saison statistique.
 *
 * Exemple :
 *
 * {
 *   season: "2025-2026",
 *   source: "MANUAL",
 *   snapshot_date: "2026-06-30",
 *   notes: "Statistiques fin de saison"
 * }
 */
export const addSeasonStat = async (
  data = {},
) => {
  if (!data.season) {
    throw new Error(
      "La saison est obligatoire.",
    );
  }

  try {
    const response = await axios.post(
      `${apiUrl}/addSeasonStat`,
      data,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la création de la saison statistique :",
      error?.response?.data || error,
    );

    throw error;
  }
};

/**
 * Ajouter une statistique à une saison.
 *
 * Exemple :
 *
 * {
 *   season_stat_id: 3,
 *   breakdown_type: "GENDER",
 *   code: "FEMALE",
 *   label: "Filles",
 *   value: 82,
 *   capacity: null,
 *   sort_order: 2
 * }
 */
export const addBreakdown = async (
  data = {},
) => {
  if (!data.season_stat_id) {
    throw new Error(
      "season_stat_id est obligatoire.",
    );
  }

  if (!data.breakdown_type) {
    throw new Error(
      "breakdown_type est obligatoire.",
    );
  }

  if (!data.code) {
    throw new Error(
      "Le code est obligatoire.",
    );
  }

  if (!data.label) {
    throw new Error(
      "Le libellé est obligatoire.",
    );
  }

  try {
    const response = await axios.post(
      `${apiUrl}/addBreakdown`,
      data,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de l'ajout de la statistique :",
      error?.response?.data || error,
    );

    throw error;
  }
};

/**
 * Ajouter plusieurs breakdowns à une saison.
 *
 * Exemple :
 *
 * {
 *   season_stat_id: 3,
 *   breakdowns: [...]
 * }
 */
export const addBreakdownsBulk = async (
  payload = {},
) => {
  const seasonStatId =
    payload.season_stat_id;

  const breakdowns = Array.isArray(
    payload.breakdowns,
  )
    ? payload.breakdowns
    : [];

  if (!seasonStatId) {
    throw new Error(
      "season_stat_id est obligatoire.",
    );
  }

  if (!breakdowns.length) {
    throw new Error(
      "Aucune statistique à enregistrer.",
    );
  }

  try {
    const response = await axios.post(
      `${apiUrl}/addBreakdownsBulk`,
      {
        season_stat_id:
          Number(seasonStatId),
        breakdowns,
      },
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de l'ajout groupé des statistiques :",
      error?.response?.data || error,
    );

    throw error;
  }
};

/**
 * Générer automatiquement le snapshot
 * statistique d'une saison.
 *
 * Exemple :
 *
 * generateSeasonStats({
 *   season: "2025-2026",
 *   snapshot_date: "2026-06-30",
 *   notes: "Fin de saison",
 *   replace: false,
 * });
 *
 * Si season n'est pas renseignée,
 * le backend utilise Settings.saison_active.
 */
export const generateSeasonStats = async (
  payload = {},
) => {
  try {
    const response = await axios.post(
      `${apiUrl}/generateSeasonStats`,
      {
        season:
          payload.season || undefined,

        snapshot_date:
          payload.snapshot_date || undefined,

        notes:
          payload.notes ?? null,

        replace:
          Boolean(payload.replace),
      },
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la génération automatique des statistiques :",
      error?.response?.data || error,
    );

    throw error;
  }
};

/* -------------------------------------------------------------------------- */
/*                                    PUT                                     */
/* -------------------------------------------------------------------------- */

/**
 * Modifier les informations générales
 * d'une saison statistique.
 */
export const editSeasonStat = async (
  id,
  data = {},
) => {
  if (!id) {
    throw new Error(
      "L'identifiant de la saison statistique est obligatoire.",
    );
  }

  try {
    const response = await axios.put(
      `${apiUrl}/editSeasonStat/${id}`,
      data,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la modification de la saison statistique :",
      error?.response?.data || error,
    );

    throw error;
  }
};

/**
 * Modifier une ligne statistique.
 */
export const editBreakdown = async (
  id,
  data = {},
) => {
  if (!id) {
    throw new Error(
      "L'identifiant de la statistique est obligatoire.",
    );
  }

  try {
    const response = await axios.put(
      `${apiUrl}/editBreakdown/${id}`,
      data,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la modification de la statistique :",
      error?.response?.data || error,
    );

    throw error;
  }
};

/**
 * Remplacer tous les breakdowns
 * d'une saison.
 *
 * Très pratique pour sauvegarder
 * un formulaire complet de saisie manuelle.
 */
export const editBreakdownsBulk = async (
  seasonStatId,
  breakdowns = [],
) => {
  if (!seasonStatId) {
    throw new Error(
      "L'identifiant de la saison statistique est obligatoire.",
    );
  }

  if (!Array.isArray(breakdowns)) {
    throw new Error(
      "breakdowns doit être un tableau.",
    );
  }

  try {
    const response = await axios.put(
      `${apiUrl}/editBreakdownsBulk/${seasonStatId}`,
      {
        breakdowns,
      },
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la modification groupée des statistiques :",
      error?.response?.data || error,
    );

    throw error;
  }
};

/* -------------------------------------------------------------------------- */
/*                                   DELETE                                   */
/* -------------------------------------------------------------------------- */

/**
 * Supprimer une saison statistique complète.
 *
 * Les breakdowns liés seront supprimés
 * grâce au ON DELETE CASCADE.
 */
export const deleteSeasonStat = async (
  id,
) => {
  if (!id) {
    throw new Error(
      "L'identifiant de la saison statistique est obligatoire.",
    );
  }

  try {
    const response = await axios.delete(
      `${apiUrl}/deleteSeasonStat/${id}`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la suppression de la saison statistique :",
      error?.response?.data || error,
    );

    throw error;
  }
};

/**
 * Supprimer une seule ligne statistique.
 */
export const deleteBreakdown = async (
  id,
) => {
  if (!id) {
    throw new Error(
      "L'identifiant de la statistique est obligatoire.",
    );
  }

  try {
    const response = await axios.delete(
      `${apiUrl}/deleteBreakdown/${id}`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la suppression de la statistique :",
      error?.response?.data || error,
    );

    throw error;
  }
};

/**
 * Supprimer tous les breakdowns
 * d'une saison sans supprimer
 * season_stats_db.
 */
export const deleteBreakdownsBySeasonStat =
  async (seasonStatId) => {
    if (!seasonStatId) {
      throw new Error(
        "L'identifiant de la saison statistique est obligatoire.",
      );
    }

    try {
      const response = await axios.delete(
        `${apiUrl}/deleteBreakdownsBySeasonStat/${seasonStatId}`,
        {
          headers: getAuthHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      console.error(
        "Erreur lors de la suppression des statistiques de la saison :",
        error?.response?.data || error,
      );

      throw error;
    }
  };